import React, { useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { calcAbsoluteEffectiveDiversity, calcGiniSimpsonIndex, calcShannonIndex, calcSpeciesRichness } from 'Util/Visualisation/VisMaths.ts'
import type { SinkConfig, SinkId, SourceId } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { isLoadingState } from '../../../../Flux/Slice/Visualization/VisualizationSliceTypes.js'
import { visualizationSlice } from '../../../../Flux/Slice/Visualization/VisualizationSlice.js'
import { StackedBarChartNext } from '../../../Component/Charts/StackedBarChartNext.js'
import { flatStackSeries, type StackSeries } from '../../../Component/Charts/StackSeries.js'
import { useTransformer } from '../Transformer/UseTransformer.tsx'
import { useSeriesSinkTable } from '../DataTable/UseSinkTable.ts'
import { useSourceView } from './UseSourceView.ts'
import { SinkContextRow, TaxaBreadcrumb } from './SinkContextRow.tsx'
import { collapseStackedSeries, OTHER_SERIES_KEY } from './Selector/CollapseSeries.ts'

export type BiodiversityMetricKey = 'speciesRichness' | 'shannon' | 'giniSimpson' | 'absoluteEffective'

export const BIODIVERSITY_LABELS: Record<BiodiversityMetricKey, string> = {
  speciesRichness: 'Species Richness',
  shannon: 'Shannon Index',
  giniSimpson: 'Gini-Simpson Index',
  absoluteEffective: 'Absolute Effective Diversity'
}

type Params = { xLabel?: string; metric: BiodiversityMetricKey }

const EMPTY_PATH: string[] = []

export default function BiodiversityMetricBase({ sourceId, config }: { sourceId: SourceId; sinkId: SinkId; config: SinkConfig<Params> }) {
  const dispatch = useDispatch()
  const { snapshot, shared, status } = useSourceView(sourceId)

  const metric = config.params.metric
  const metricLabel = BIODIVERSITY_LABELS[metric]

  const selectedPath = shared?.selectedPath ?? EMPTY_PATH
  const gradeThreshold = shared?.gradeThreshold ?? 0
  const xLabel = config.params?.xLabel ?? 'Groups'
  const depth = selectedPath.length

  const bucketsRes = useTransformer(sourceId, undefined, 'kronaFilteredBuckets', {
    selectedPath,
    gradeThreshold
  })

  const { data, stackKeys, stackSeries } = useMemo(() => {
    if (bucketsRes.status !== 'ready') {
      return { data: [], stackKeys: [] as string[], stackSeries: [] as StackSeries[] }
    }

    const toMetric = (mags: number[]) => {
      switch (metric) {
        case 'speciesRichness':
          return calcSpeciesRichness(mags)
        case 'shannon':
          return calcShannonIndex(mags)
        case 'giniSimpson':
          return calcGiniSimpsonIndex(mags)
        case 'absoluteEffective':
          return calcAbsoluteEffectiveDiversity(mags)
      }
    }

    const rows = bucketsRes.value.map((bucket) => {
      const byTaxon = bucket.values.reduce<Record<string, number[]>>((acc, v) => {
        const k = v.taxa[depth] ?? '(n/a)'
        ;(acc[k] ||= []).push(v.magnitude ?? 0)
        return acc
      }, {})

      const metricPerTaxon: Record<string, number> = Object.fromEntries(
        Object.entries(byTaxon).map(([taxon, mags]) => [taxon, toMetric(mags)])
      )

      return { groupName: bucket.bucketLabel, data: metricPerTaxon }
    })

    const union = new Set<string>()
    rows.forEach((r) => Object.keys(r.data).forEach((k) => union.add(k)))
    const keys = Array.from(union).sort((a, b) => a.localeCompare(b)) // keep stable

    const expanded = rows.map((r) => ({
      xAxisvalue: r.groupName,
      xAxisLabel: xLabel,
      ...keys.reduce<Record<string, number>>((acc, k) => {
        acc[k] = r.data[k] ?? 0
        return acc
      }, {})
    }))

    // Collapse the sub-pixel long tail into "Other" (same STACKED_BAR_CAP knob as the taxa
    // stacked bar). This is what made a single render ~1s — hundreds of uncapped Recharts bars.
    const collapsed = collapseStackedSeries(expanded, keys)

    return { data: collapsed.data, stackKeys: collapsed.stackKeys, stackSeries: flatStackSeries(collapsed.stackKeys) }
  }, [bucketsRes, metric, depth, xLabel])

  // The per-taxon metric values behind the bars, for the container's "Numbers" view. No sample
  // counts: this sink aggregates the buckets itself and never carries one.
  const tableRows = useMemo(
    () =>
      (data as Array<Record<string, number | string>>).map((row) => ({
        label: String(row.xAxisvalue ?? ''),
        values: row as Record<string, number>
      })),
    [data]
  )

  useSeriesSinkTable({
    sourceId,
    selectedPath,
    gradeThreshold,
    inTraitMode: false,
    ready: bucketsRes.status === 'ready' && tableRows.length > 0,
    categories: stackKeys,
    rows: tableRows,
    groupLabel: xLabel,
    categoryLabel: 'Taxon',
    valueLabel: metricLabel
  })

  const onBarClick = (taxon: string) => {
    if (taxon === OTHER_SERIES_KEY) return // aggregate remainder — not a drillable taxon
    const nextPath = [...selectedPath, taxon]
    dispatch(visualizationSlice.actions.setTaxaZoom({ sourceId, path: nextPath }))
  }

  if (!snapshot) {
    return (
      <div className="text-sm opacity-70 p-3">
        Waiting for data… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}
      </div>
    )
  }

  if (bucketsRes.status === 'idle' || bucketsRes.status === 'loading') {
    return (
      <div className="text-sm opacity-70 p-3">Computing… {isLoadingState(status) && <span className="loading loading-dots loading-xs" />}</div>
    )
  }

  if (!stackSeries.length) {
    return <div className="text-sm opacity-70 p-3">No biodiversity data for current selection.</div>
  }

  return (
    <div className="flex flex-col h-full">
      {selectedPath.length > 0 && (
        <SinkContextRow>
          <TaxaBreadcrumb
            path={selectedPath}
            onBack={() => dispatch(visualizationSlice.actions.goBackOneTaxaLevel({ sourceId }))}
            onReset={() => dispatch(visualizationSlice.actions.resetTaxaZoom({ sourceId }))}
          />
        </SinkContextRow>
      )}
      <StackedBarChartNext
        dataKey="xAxisvalue"
        data={data}
        stackSeries={stackSeries}
        onBarClick={onBarClick}
        ylabel={metricLabel}
        height={'100%'}
        xlabel={xLabel}
      />
    </div>
  )
}
